import type {
  AddReportWidgetsRequest,
  CampaignPost,
  CreateReportRequest,
  RemoveReportWidgetRequest,
  ReorderReportWidgetsRequest,
  Report,
  ReportRef,
  SaveReportCampaignRequest,
  SearchCampaignPostsRequest,
  WidgetId,
} from '@/data-sources/contract';
import { api, overrideExisting } from '@/store/api/api';
import { fromSource, type Sourced } from '@/store/api/fromSource';
import { tag } from '@/store/api/tags';

export const reportsApi = api.injectEndpoints({
  overrideExisting,
  endpoints: (build) => ({
    /** The widgets a report can store (`null`: any). A capability of the source — kept for the session. */
    storableWidgets: build.query<WidgetId[] | null, Sourced<void>>({
      queryFn: fromSource((source, _params, ctx) => source.storableWidgets(ctx)),
      keepUnusedDataFor: Infinity,
    }),
    listReports: build.query<Report[], Sourced<void>>({
      queryFn: fromSource((source, _params, ctx) => source.listReports(ctx)),
      providesTags: (_r, _e, arg) => [tag('Report', arg.source)],
    }),
    getReport: build.query<Report, Sourced<ReportRef>>({
      queryFn: fromSource((source, params, ctx) => source.getReport(params, ctx)),
      providesTags: (_r, _e, arg) => [tag('Report', arg.source, arg.params.id)],
    }),
    createReport: build.mutation<Report, Sourced<CreateReportRequest>>({
      queryFn: fromSource((source, params, ctx) => source.createReport(params, ctx)),
      invalidatesTags: (_r, e, arg) => (e ? [] : [tag('Report', arg.source)]),
    }),
    deleteReport: build.mutation<null, Sourced<ReportRef>>({
      queryFn: fromSource(async (source, params, ctx) => {
        await source.deleteReport(params, ctx);
        return null;
      }),
      invalidatesTags: (_r, e, arg) => (e ? [] : [tag('Report', arg.source)]),
    }),
    addReportWidgets: build.mutation<Report, Sourced<AddReportWidgetsRequest>>({
      queryFn: fromSource((source, params, ctx) => source.addReportWidgets(params, ctx)),
      invalidatesTags: (_r, e, arg) =>
        e ? [] : [tag('Report', arg.source), tag('Report', arg.source, arg.params.id)],
    }),
    removeReportWidget: build.mutation<Report, Sourced<RemoveReportWidgetRequest>>({
      queryFn: fromSource((source, params, ctx) => source.removeReportWidget(params, ctx)),
      invalidatesTags: (_r, e, arg) =>
        e ? [] : [tag('Report', arg.source), tag('Report', arg.source, arg.params.id)],
    }),
    reorderReportWidgets: build.mutation<Report, Sourced<ReorderReportWidgetsRequest>>({
      queryFn: fromSource((source, params, ctx) => source.reorderReportWidgets(params, ctx)),
      invalidatesTags: (_r, e, arg) =>
        e ? [] : [tag('Report', arg.source), tag('Report', arg.source, arg.params.id)],
    }),
    saveReportCampaign: build.mutation<Report, Sourced<SaveReportCampaignRequest>>({
      queryFn: fromSource((source, params, ctx) => source.saveReportCampaign(params, ctx)),
      invalidatesTags: (_r, e, arg) =>
        e ? [] : [tag('Report', arg.source), tag('Report', arg.source, arg.params.id)],
    }),
    /**
     * Read-only, so a query rather than a mutation — but it is only ever run
     * from the modal's `Search` button, through the lazy hook. Nothing here
     * fires on mount.
     */
    searchCampaignPosts: build.query<CampaignPost[], Sourced<SearchCampaignPostsRequest>>({
      queryFn: fromSource((source, params, ctx) => source.searchCampaignPosts(params, ctx)),
    }),
  }),
});

export const {
  useStorableWidgetsQuery,
  useListReportsQuery,
  useGetReportQuery,
  useCreateReportMutation,
  useDeleteReportMutation,
  useAddReportWidgetsMutation,
  useRemoveReportWidgetMutation,
  useReorderReportWidgetsMutation,
  useSaveReportCampaignMutation,
  useSearchCampaignPostsQuery,
} = reportsApi;
